import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { contentAPI } from '../services/api'
import type { ContentMoment } from '../types'
import { LOCK_CALL_PATH } from '../routes'
import { useAuthStore } from '../store/authStore'
import { clipFactoryUrl } from './clips/clipFactoryApi'
import { apiErrorMessage } from '../utils/apiError'
import { NetworkPicks, PackDone, type PackResult, writePack } from './ContentPack'

const ago = (iso: string | null): string => {
  if (!iso) return ''
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000))
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} h ago`
  return `${Math.round(hours / 24)} d ago`
}

const followUpTopic = (m: ContentMoment) =>
  `A follow-up to my post that did well${m.source_text ? `: "${m.source_text.slice(0, 280)}"` : ''}. ` +
  'Take the same idea one step further, in the same voice.'

const ACTION_LABEL: Record<ContentMoment['kind'], string> = {
  call_locked: 'Write the post',
  call_closed: 'Write the post',
  recap_ready: 'Draft the recap',
  post_did_well: 'Write a follow-up',
  call_moved: 'Write the post',
  market_move: 'Write the post',
  market_mood: 'Write the post',
  first_call: '',
}

/** One moment: what happened, and one tap to a draft. */
const MomentCard = ({ moment }: { moment: ContentMoment }) => {
  // Clips is for admins for now, and only where Clip Factory is set up.
  const clipIt = useAuthStore((state) => !!state.user?.is_superuser) && !!clipFactoryUrl()
  const [networks, setNetworks] = useState<string[]>([moment.content_type || 'twitter'])
  const [state, setState] = useState<'idle' | 'busy'>('idle')
  const [result, setResult] = useState<PackResult | null>(null)
  const drafted = result ? result.drafts.length > 0 : moment.drafted_content_id !== null

  const writeOne = async (contentType: string) => {
    if (moment.kind === 'recap_ready') {
      const recap = await contentAPI.dailyRecap(contentType as 'twitter' | 'linkedin' | 'instagram' | 'blog')
      if (!recap.drafted || !recap.content) throw new Error(recap.reason || 'Nothing to recap yet.')
      return recap.content
    }
    if (moment.kind === 'post_did_well') {
      return contentAPI.generate({ content_type: contentType, topic: followUpTopic(moment) })
    }
    if (moment.topic) {
      // A market moment: the writer quotes the live price or reading, not the card's.
      return contentAPI.generate({ content_type: contentType, topic: moment.topic })
    }
    return contentAPI.generate({ content_type: contentType, call_id: moment.call_id })
  }

  const draft = async () => {
    setState('busy')
    try {
      setResult(
        await writePack(networks, writeOne, (e) =>
          e instanceof Error && !('response' in e) ? e.message : apiErrorMessage(e, "Couldn't write it.")
        )
      )
    } finally {
      setState('idle')
    }
  }

  if (moment.kind === 'first_call') {
    return (
      <li className="rounded-lg border border-dashed border-gray-300 p-3" data-testid="moment">
        <p className="font-medium text-gray-900">{moment.title}</p>
        <p className="mt-0.5 text-sm text-gray-600">{moment.detail}</p>
        <Link
          to={LOCK_CALL_PATH}
          className="mt-2 inline-block rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
        >
          Lock a call
        </Link>
      </li>
    )
  }

  return (
    <li className="rounded-lg border border-gray-200 bg-white p-3" data-testid="moment">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <p className="font-medium text-gray-900">{moment.title}</p>
        <span className="text-xs text-gray-500">{ago(moment.occurred_at)}</span>
      </div>
      <p className="mt-0.5 text-sm text-gray-600">{moment.detail}</p>
      <div className="mt-2">
        {/* A recap is the day's: no newsletter issue from it here. */}
        <NetworkPicks picked={networks} onChange={setNetworks} without={moment.kind === 'recap_ready' ? ['newsletter'] : []} />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
        <button
          type="button"
          onClick={draft}
          disabled={state === 'busy' || networks.length === 0}
          className={`rounded-md px-3 py-1.5 text-sm font-medium disabled:opacity-50 ${
            drafted
              ? 'border border-gray-300 text-gray-700 hover:bg-gray-50'
              : 'bg-primary-600 text-white hover:bg-primary-700'
          }`}
        >
          {state === 'busy' ? 'Writing…' : drafted ? 'Write another' : ACTION_LABEL[moment.kind]}
        </button>
        {!result && moment.drafted_content_id && (
          <Link to="/content" state={{ openIds: [moment.drafted_content_id] }} className="text-primary-600 hover:underline">
            Drafted: open in Library
          </Link>
        )}
        {clipIt && moment.kind === 'call_closed' && moment.call_id !== undefined && (
          <Link to={`/content/clips?call=${moment.call_id}`} className="text-primary-600 hover:underline" data-testid="clip-it">
            Clip it
          </Link>
        )}
        {moment.receipt_path && (
          <a href={moment.receipt_path} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:underline">
            Receipt
          </a>
        )}
        {moment.post_url && (
          <a href={moment.post_url} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:underline">
            The post
          </a>
        )}
      </div>
      {result && (
        <div className="mt-1">
          <PackDone result={result} />
        </div>
      )}
    </li>
  )
}

/**
 * The top of Create: what's worth posting about now, from the creator's own
 * record (calls locked and closed, today's recap, a post that did well), then
 * the market (an open call or a major coin that moved, an extreme Fear & Greed
 * reading), each one tap from a draft. The topic box below stays for anything else.
 */
const MomentsPanel = () => {
  const [moments, setMoments] = useState<ContentMoment[] | null>(null)

  useEffect(() => {
    let stopped = false
    // Optional: Create works without it.
    Promise.resolve()
      .then(() => contentAPI.moments())
      .then((list) => !stopped && setMoments(list))
      .catch(() => !stopped && setMoments([]))
    return () => {
      stopped = true
    }
  }, [])

  if (!moments || moments.length === 0) return null

  return (
    <section aria-label="Worth posting about" className="rounded-lg bg-gray-50 p-4">
      <h2 className="text-lg font-semibold text-gray-900">Worth posting about</h2>
      <p className="text-sm text-gray-600">
        From your own record, then the market&apos;s live prices. Each draft opens in Library to check before you post.
      </p>
      <ul className="mt-3 space-y-2">
        {moments.map((m) => (
          <MomentCard key={m.id} moment={m} />
        ))}
      </ul>
    </section>
  )
}

export default MomentsPanel
